import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Check, CheckCircle2, CreditCard, LockKeyhole } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SubscriptionPlan, formatCurrency, validatePromoCode } from '../data/subscriptionPlans';
import { useTheme } from '../contexts/ThemeContext';
import { PaymentResultScreen, PaymentFailure } from '../components/billing/PaymentResultScreen';
interface ProCheckoutScreenProps {
  plan: SubscriptionPlan;
  hasTrialOffer?: boolean;
  onBack: () => void;
  onSuccess: () => void;
  promoCode?: string;
  /** Leaves checkout without paying (from the "Payment cancelled" screen). */
  onExit?: () => void;
  /** Shows the internal gateway-outcome switch. Never shown to real users. */
  showDemoTools?: boolean;
}
type SimulatedOutcome = 'success' | PaymentFailure;
const OUTCOMES: {
  id: SimulatedOutcome;
  label: string;
}[] = [{
  id: 'success',
  label: 'Success'
}, {
  id: 'declined',
  label: 'Declined'
}, {
  id: 'cancelled',
  label: 'Cancelled'
}];
export function ProCheckoutScreen({
  plan,
  hasTrialOffer = true,
  onBack,
  onSuccess,
  promoCode = '',
  onExit,
  showDemoTools = false
}: ProCheckoutScreenProps) {
  const {
    isLight
  } = useTheme();
  const [processing, setProcessing] = useState(false);
  const [simulatedOutcome, setSimulatedOutcome] = useState<SimulatedOutcome>('success');
  const [failure, setFailure] = useState<PaymentFailure | null>(null);
  const [cardholderName, setCardholderName] = useState('Alex Johnson');
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242');
  const [expiry, setExpiry] = useState('12/28');
  const [cvc, setCvc] = useState('123');
  const [authorized, setAuthorized] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const appliedPromo = validatePromoCode(promoCode);
  const firstMonthPrice = appliedPromo ? plan.price * (1 - appliedPromo.firstMonthDiscountPercent / 100) : plan.price;
  const firstCharge = new Date();
  if (hasTrialOffer) firstCharge.setDate(firstCharge.getDate() + 3);
  const firstChargeLabel = firstCharge.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const muted = isLight ? 'text-slate-500' : 'text-zinc-400';
  const strong = isLight ? 'text-slate-900' : 'text-white';
  const submit = () => {
    const digits = cardNumber.replace(/\s/g, '');
    if (!cardholderName.trim() || digits.length < 15 || !/^\d{2}\/\d{2}$/.test(expiry) || cvc.length < 3) {
      setPaymentError('Enter valid card details to continue.');
      return;
    }
    if (!authorized) {
      setPaymentError('Please authorize the subscription to continue.');
      return;
    }
    setPaymentError('');
    setProcessing(true);
    // Replace with the real gateway response. The demo switch picks the outcome.
    window.setTimeout(() => {
      if (simulatedOutcome === 'success') {
        onSuccess();
        return;
      }
      setProcessing(false);
      setFailure(simulatedOutcome);
    }, 1600);
  };
  const cardLast4 = cardNumber.replace(/\s/g, '').slice(-4) || '••••';
  const rows: {
    label: string;
    value: string;
    emphasis?: boolean;
  }[] = [...(hasTrialOffer ? [{
    label: 'Due today',
    value: '$0.00',
    emphasis: true
  }, {
    label: 'Free trial',
    value: '3 days'
  }] : []), {
    label: hasTrialOffer ? `First charge · ${firstChargeLabel}` : 'Due today',
    value: `${formatCurrency(firstMonthPrice)}${appliedPromo ? ` (${appliedPromo.code})` : ''}`,
    emphasis: !hasTrialOffer
  }, {
    label: 'Then renews monthly',
    value: `${formatCurrency(plan.price)}/mo`
  }, {
    label: 'Extra minutes',
    value: `${formatCurrency(plan.overageRate)}/min`
  }];
  return <div className={`relative flex h-full flex-col overflow-hidden ${isLight ? 'bg-slate-50' : 'bg-black'}`} data-id="element-2854" data-mp="3c2svohbt3bzo">
      <header className={`flex items-center justify-between border-b px-5 py-4 ${isLight ? 'border-slate-200 bg-white' : 'border-white/10 bg-black'}`} data-id="element-2855" data-mp="1mp5xuujfwyo4">
        <button type="button" onClick={onBack} disabled={processing} aria-label="Back to consent" className={`text-sm font-semibold transition-colors disabled:opacity-40 ${isLight ? 'text-slate-500 hover:text-slate-900' : 'text-zinc-400 hover:text-white'}`} data-id="element-2856" data-mp="1k3yd01r4prhx">
          Back
        </button>
        <span className={`text-xs font-bold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-2857" data-mp="1y2xv0qk4z854">Payment</span>
        <span className={`text-xs font-semibold ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2858" data-mp="10hii4geb6tdr">3 of 3</span>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-6" data-id="element-2859" data-mp="1advfbo92bpd9">
        <div className="mx-auto max-w-sm space-y-4" data-id="element-2860" data-mp="1acakdxiu64ev">
          {/* Billing summary */}
          <section className={`rounded-2xl border p-5 ${isLight ? 'border-slate-200 bg-white shadow-sm' : 'border-white/10 bg-white/5'}`} data-id="element-2861" data-mp="1f5dd63fsh9u0">
            <p className={`text-xs font-bold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2862" data-mp="mo0ykvlmhgnq">
              {plan.name} · {plan.minutes.toLocaleString()} min/month
            </p>
            <h1 className={`mt-1 text-xl font-bold ${strong}`} data-id="element-2863" data-mp="f2fbx2iazvry">
              {hasTrialOffer ? 'Start your free trial' : 'Start your subscription'}
            </h1>
            <dl className={`mt-4 divide-y border-t ${isLight ? 'divide-slate-100 border-slate-100' : 'divide-white/10 border-white/10'}`} data-id="element-2864" data-mp="550vh2wdr6zn">
              {rows.map(r => <div key={r.label} className="flex items-center justify-between gap-3 py-2.5" data-id="element-2865" data-mp="3sbju742laxu2">
                  <dt className={`text-[13px] ${muted}`} data-id="element-2866" data-mp="3nkbfbsihqk9i">{r.label}</dt>
                  <dd className={`text-[13px] font-semibold tabular-nums ${r.emphasis ? isLight ? 'text-vexu-700' : 'text-vexu' : strong}`} data-id="element-2867" data-mp="3ili466qof5ci">
                    {r.value}
                  </dd>
                </div>)}
            </dl>
            {appliedPromo && <p className={`mt-2 flex items-center gap-1.5 text-[11px] font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2868" data-mp="38p5a37r7yph8">
                <CheckCircle2 className="h-3.5 w-3.5" data-id="element-2869" data-mp="2u03vr1miai20" />
                {appliedPromo.description}
              </p>}
          </section>

          {/* Card */}
          <section aria-labelledby="payment-title" className={`rounded-2xl border p-5 ${isLight ? 'border-slate-200 bg-white shadow-sm' : 'border-white/10 bg-white/5'}`} data-id="element-2870" data-mp="2yxc7okh29put">
            <div className="mb-4 flex items-center justify-between" data-id="element-2871" data-mp="3dol0t3k69tcp">
              <div className="flex items-center gap-2.5" data-id="element-2872" data-mp="3dpeaacaimwyj">
                <div className={`grid h-9 w-9 place-items-center rounded-xl ${isLight ? 'bg-slate-100 text-slate-700' : 'bg-white/10 text-zinc-300'}`} data-id="element-2873" data-mp="3hqg2i960m8r0">
                  <CreditCard className="h-4 w-4" data-id="element-2874" data-mp="33wyunlmoykgt" />
                </div>
                <div data-id="element-2875" data-mp="38wbhw7hh5tjx">
                  <h2 id="payment-title" className={`text-sm font-bold ${strong}`} data-id="element-2876" data-mp="38vs0crvql76f">
                    Credit or debit card
                  </h2>
                  <p className={`text-[11px] ${muted}`} data-id="element-2877" data-mp="591aodessr1v">Secure, encrypted payment</p>
                </div>
              </div>
              <LockKeyhole className={`h-4 w-4 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2878" data-mp="em1sev7q771k" />
            </div>
            <div className="space-y-3" data-id="element-2879" data-mp="ajx2a6ghksjo">
              <Field label="Name on card" value={cardholderName} onChange={v => {
              setCardholderName(v);
              setPaymentError('');
            }} autoComplete="cc-name" data-id="element-2880" data-mp="odxt8e175b9h" />
              <Field label="Card number" value={cardNumber} onChange={v => {
              const d = v.replace(/\D/g, '').slice(0, 16);
              setCardNumber(d.replace(/(.{4})/g, '$1 ').trim());
              setPaymentError('');
            }} inputMode="numeric" autoComplete="cc-number" data-id="element-2881" data-mp="jf3998rhtgns" />
              <div className="grid grid-cols-2 gap-3" data-id="element-2882" data-mp="pwrbnyiwt31">
                <Field label="Expiry" value={expiry} onChange={v => {
                const d = v.replace(/\D/g, '').slice(0, 4);
                setExpiry(d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d);
                setPaymentError('');
              }} placeholder="MM/YY" inputMode="numeric" autoComplete="cc-exp" data-id="element-2883" data-mp="1h10p0yehn7w1" />
                <Field label="CVC" value={cvc} onChange={v => {
                setCvc(v.replace(/\D/g, '').slice(0, 4));
                setPaymentError('');
              }} inputMode="numeric" autoComplete="cc-csc" data-id="element-2884" data-mp="3kac393xzg9c0" />
              </div>
            </div>
          </section>

          {/* Explicit authorization */}
          <div role="checkbox" aria-checked={authorized} tabIndex={0} onClick={() => {
          setAuthorized(v => !v);
          setPaymentError('');
        }} onKeyDown={e => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            setAuthorized(v => !v);
            setPaymentError('');
          }
        }} className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${authorized ? 'border-vexu bg-vexu/10' : isLight ? 'border-slate-200 bg-white hover:border-slate-300' : 'border-white/10 bg-white/5 hover:border-white/20'}`} data-id="element-2885" data-mp="3uaw9ym49u61m">
            <span className={`mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-md border-2 ${authorized ? 'border-vexu bg-vexu text-white' : isLight ? 'border-slate-300 bg-white' : 'border-white/30'}`} data-id="element-2886" data-mp="1mo2uygc4ppli">
              {authorized && <Check className="h-3.5 w-3.5" strokeWidth={3} data-id="element-2887" data-mp="2acm22sjfzwc0" />}
            </span>
            <span className={`text-xs leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-2888" data-mp="2aep1222kozsv">
              {hasTrialOffer ? <>
                  I authorize Vexu to charge this card <strong data-id="element-2889" data-mp="yvcounqwq0j5">{formatCurrency(firstMonthPrice)}</strong> on {firstChargeLabel}
                  {appliedPromo ? <>, then {formatCurrency(plan.price)}</> : null} and every month after, plus any extra minutes, until I cancel. I can cancel anytime in the app before the trial ends and won't be charged.
                </> : <>
                  I authorize Vexu to charge this card <strong data-id="element-2890" data-mp="2lj1fn3dh1559">{formatCurrency(firstMonthPrice)}</strong> today
                  {appliedPromo ? <>, then {formatCurrency(plan.price)}</> : null} every month, plus any extra minutes, until I cancel.
                </>}
            </span>
          </div>

          {paymentError && <p role="alert" className={`flex items-center gap-1.5 text-xs ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-2891" data-mp="34g2hifbqap4z">
              <AlertCircle className="h-3.5 w-3.5" data-id="element-2892" data-mp="2zgpy64k2g075" />
              {paymentError}
            </p>}
        </div>
      </main>

      <footer className={`border-t px-6 pb-7 pt-4 ${isLight ? 'border-slate-200 bg-white' : 'border-white/10 bg-black'}`} data-id="element-2893" data-mp="1a2jpfl0gvyr2">
        {showDemoTools && <div className="mb-3 flex items-center gap-2" data-id="element-2894" data-mp="3gps6nzbj2ljk">
            <span className={`flex-shrink-0 text-[10px] font-bold ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2895" data-mp="3gr254pmalvqq">
              Demo · Gateway result
            </span>
            <div role="radiogroup" aria-label="Simulated payment result" className={`grid flex-1 grid-cols-3 gap-0.5 rounded-lg border border-dashed p-0.5 ${isLight ? 'border-slate-300' : 'border-white/15'}`} data-id="element-2896" data-mp="37x71jm0tkdd7">
              {OUTCOMES.map(o => {
            const selected = simulatedOutcome === o.id;
            return <button key={o.id} type="button" role="radio" aria-checked={selected} onClick={() => setSimulatedOutcome(o.id)} className={`rounded-md py-1 text-[11px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${selected ? o.id === 'success' ? 'bg-vexu text-white' : o.id === 'declined' ? 'bg-red-500 text-white' : isLight ? 'bg-slate-700 text-white' : 'bg-white text-black' : isLight ? 'text-slate-500 hover:text-slate-800' : 'text-zinc-400 hover:text-white'}`} data-id="element-2897" data-mp="2p05tttfvrz5k">
                    {o.label}
                  </button>;
          })}
            </div>
          </div>}
        <Button fullWidth onClick={submit} disabled={processing || !authorized} className="py-3.5" data-id="element-2898" data-mp="2ivhj12sl662d">
          {hasTrialOffer ? 'Start 3-Day Free Trial' : `Pay ${formatCurrency(firstMonthPrice)} & start`}
        </Button>
      </footer>

      <AnimatePresence data-id="element-2899" data-mp="2a24gra8mts3m">
        {processing && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.2
      }} className={`absolute inset-0 z-[60] grid place-items-center p-8 backdrop-blur-xl ${isLight ? 'bg-white/95' : 'bg-black/95'}`} data-id="element-2900" data-mp="254bdaoyprc71">
            <div className="text-center" data-id="element-2901" data-mp="355zjzaok2077">
              <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'linear'
          }} className="mx-auto h-12 w-12 rounded-full border-2 border-vexu/20 border-t-vexu" data-id="element-2902" data-mp="357ar9kh7lq32" />
              <p className={`mt-6 font-bold ${strong}`} data-id="element-2903" data-mp="3j60lmscn9bnq">{hasTrialOffer ? 'Activating your trial' : 'Activating your subscription'}</p>
              <p className={`mt-1 text-sm ${muted}`} data-id="element-2904" data-mp="3o1y4b5bdqu3o">Setting up Vexu {plan.name}…</p>
            </div>
          </motion.div>}
        {failure && <PaymentResultScreen key="payment-result" outcome={failure} cardLast4={cardLast4} onRetry={() => setFailure(null)} onUseAnotherCard={() => {
        setFailure(null);
        setCardNumber('');
        setExpiry('');
        setCvc('');
      }} onExit={() => {
        setFailure(null);
        (onExit ?? onBack)();
      }} data-id="element-2905" data-mp="1o7rlv6ibe8h" />}
      </AnimatePresence>
    </div>;
}
interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
  autoComplete?: string;
}
function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode = 'text',
  autoComplete
}: FieldProps) {
  const {
    isLight
  } = useTheme();
  return <label className="block" data-id="element-2906" data-mp="3f8brq20e2fyl">
      <span className={`text-[11px] font-semibold ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-2907" data-mp="3b6gga4oqum73">{label}</span>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} inputMode={inputMode} autoComplete={autoComplete} className={`mt-1.5 w-full rounded-xl border px-3.5 py-3 text-sm outline-none transition-colors ${isLight ? 'border-slate-200 bg-slate-50 text-slate-900 focus:border-vexu focus:ring-2 focus:ring-vexu/15' : 'border-white/10 bg-black/20 text-white focus:border-vexu focus:ring-1 focus:ring-vexu/20'}`} data-id="element-2908" data-mp="3p6r6d5i8bwr7" />
    </label>;
}